import { useCallback, useEffect, useRef, useState } from 'react';
import notificationService from '../services/notificationService';
import { authService } from '../services/authService';

const POLL_INTERVAL_MS = 20000;
const STORAGE_KEY = 'kardex_notification_seen_ids';

function loadSeenIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveSeenIds(set) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

export function useKardexRecalculateNotifications() {
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const seenIdsRef = useRef(loadSeenIds());
  const pollingRef = useRef(null);

  const processNotifications = useCallback(async () => {
    if (!authService.isAuthenticated()) return;

    try {
      const response = await notificationService.list();
      const items = Array.isArray(response?.data) ? response.data : [];

      for (const item of items) {
        if (!item?.id || seenIdsRef.current.has(item.id)) continue;
        const supportedTypes = ['kardex_recalculate_completed', 'export_completed'];
        if (!supportedTypes.includes(item.type)) continue;
        if (item.read_at) {
          seenIdsRef.current.add(item.id);
          continue;
        }

        seenIdsRef.current.add(item.id);
        saveSeenIds(seenIdsRef.current);

        setToast({
          isOpen: true,
          message:
            item.message ||
            (item.type === 'export_completed'
              ? 'Datos de guía listos para PDF o impresión.'
              : 'Recálculo de kardex completado.'),
          type: item.toast_type || 'success',
        });

        await notificationService.markAsRead(item.id);
        break;
      }
    } catch (error) {
      console.error('Error al consultar notificaciones de kardex:', error);
    }
  }, []);

  useEffect(() => {
    if (!authService.isAuthenticated()) return undefined;

    processNotifications();
    pollingRef.current = setInterval(processNotifications, POLL_INTERVAL_MS);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [processNotifications]);

  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return { toast, closeToast };
}
